import { Popover, PopoverProps } from 'antd';
import iconDiamonds from '@/components/diamonds/images/icon_gem.png';
import Image from 'next/image';
import { twMerge } from 'tailwind-merge';
import { BalooBhaifont } from '@/lib/utils';
import useTranslation from 'next-translate/useTranslation';
import { Popover as HeaderPopover } from '@flyer/components';
import { useAtomValue } from 'jotai';
import { ieltsThemeAtom } from '@/store/home';
import { IeltsTheme } from '@/types';

type Props = PopoverProps & {
  diamonds?: number;
  freeDiamonds?: number;
};

const RowItem = ({
  title,
  value,
  tooltip,
}: {
  title: string;
  value?: number;
  tooltip?: string;
}) => {
  return (
    <HeaderPopover content={tooltip} key={title} zIndex={3001}>
      <div className="flex items-center justify-between py-2 px-4 max-xl:px-2 max-xl:py-1">
        <span className="text-sm text-white font-bold max-xl:text-xs max-sm:text-[10px]">
          {title}
        </span>
        <span
          className={twMerge(
            'text-lg text-white max-xl:text-sm max-sm:text-xs',
            BalooBhaifont.className,
          )}
        >
          {value}{' '}
          <Image
            src={iconDiamonds}
            alt="Diamonds"
            width={70}
            height={70}
            className="w-[22px] h-[19px] max-xl:w-4 max-xl:h-4 object-contain inline-block ml-1 align-middle"
          />
        </span>
      </div>
    </HeaderPopover>
  );
};

export default function DiamondPopover({ diamonds, freeDiamonds, children, ...props }: Props) {
  const { t } = useTranslation('diamonds');
  // GAMIFICATION keeps its own palette; the nine classic themes read `accent`.
  const ieltsTheme = useAtomValue(ieltsThemeAtom);
  const checkColor =
    ieltsTheme === IeltsTheme.GAMIFICATION
      ? ' #001A90'
      : 'var(--ielts-accent)';
  const checkColor2 =
    ieltsTheme === IeltsTheme.GAMIFICATION
      ? ' #1A4AC0'
      : 'var(--ielts-accent)';

  const content = (
    <div className="w-[220px] max-xl:w-[150px]">
      <RowItem
        title={t('diamonds.purchased')}
        tooltip={t('diamonds.purchased_tooltip')}
        value={diamonds}
      />

      <div
        className="w-full h-[1px]"
        style={{
          background: 'rgba(#fff, .2)',
        }}
      />

      <RowItem
        title={t('diamonds.rewarded')}
        tooltip={t('diamonds.rewarded_tooltip')}
        value={freeDiamonds}
      />
    </div>
  );

  return (
    <Popover
      content={content}
      placement="bottom"
      arrow={false}
      trigger={['hover']}
      zIndex={3000}
      getPopupContainer={(trigger) => trigger.parentElement || document.body}
      styles={{
        body: {
          padding: 0,
          background: checkColor2,
          border: `1px solid ${checkColor}`,
          boxShadow: `0 4px 0 0 ${checkColor}`,
        },
      }}
      {...props}
    >
      <div>{children}</div>
    </Popover>
  );
}
